// Libs
import { Box, FormHelperText, Grid2, IconButton, Stack, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { Fragment, memo, useEffect } from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { TotalFieldPOProduct } from '~/components/module/job';
// Others
import { BaseInput } from '~/components/common';
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING, REG_EXP } from '~/utils/constants/common';
import { formatInputDecimal } from '~/utils/helper/common';
import { IFormPO } from '~/utils/interface/po';
import { defaultValueProductPO } from '../formPOJob/helper';
// Styles, images, icons
import { icons } from '~/assets';
import styles from './FieldPOProduct.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const FieldPOProduct = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/po']);
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<IFormPO>();

  const {
    fields: fieldsProducts,
    append: appendProducts,
    remove: removeProducts,
  } = useFieldArray({ control, name: 'products' });

  const products = useWatch({ control, name: `products` });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!products || products.length === DEFAULT_NUMBER_ZERO) {
      setValue(`total`, DEFAULT_NUMBER_ZERO);
      return;
    }
    const total = (products ?? [])?.reduce((total, po) => {
      return total + Number(po.cost) * Number(po.quantity);
    }, DEFAULT_NUMBER_ZERO);
    setValue(`total`, total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleAddItemProducts = () => {
    appendProducts(defaultValueProductPO);
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} className={cx('container')} id='fieldPOProductComponent'>
      {fieldsProducts && fieldsProducts.length > DEFAULT_NUMBER_ZERO && (
        <Grid2 container size={12} rowSpacing={'24px'} columnSpacing={'24px'}>
          {fieldsProducts?.map((item, index) => {
            return (
              <Fragment key={index}>
                <Grid2 size={3}>
                  <Controller
                    name={`products.${index}.name`}
                    control={control}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <BaseInput
                          label={t('form_po_product_name_label')}
                          value={value || EMPTY_STRING}
                          onChange={onChange}
                          errorMessage={errors?.products?.[index]?.name?.message}
                        />
                      );
                    }}
                  />
                </Grid2>

                <Grid2 size={3}>
                  <Controller
                    name={`products.${index}.quantity`}
                    control={control}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <BaseInput
                          label={t('form_po_qty_label')}
                          value={String(value)}
                          onChange={(event) => {
                            const inputValue = event.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                            const number = inputValue ? Number(inputValue).toString() : EMPTY_STRING;
                            setValue(`products.${index}.quantity`, Number(number), { shouldDirty: true });
                          }}
                          errorMessage={errors?.products?.[index]?.quantity?.message}
                        />
                      );
                    }}
                  />
                </Grid2>

                <Grid2 size={3}>
                  <Controller
                    name={`products.${index}.cost`}
                    control={control}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <BaseInput
                          label={t('form_po_cost_label')}
                          value={String(value)}
                          onChange={(event) => {
                            const newValue = formatInputDecimal(
                              event.target.value || DEFAULT_NUMBER_ZERO.toString()
                            );
                            onChange(newValue);
                          }}
                          errorMessage={errors?.products?.[index]?.cost?.message}
                        />
                      );
                    }}
                  />
                </Grid2>

                <Grid2 size={3} className={cx('totalField')}>
                  <TotalFieldPOProduct index={index} />

                  <Grid2 size='auto' className={cx('removeBtn')}>
                    <IconButton className={cx('closeField')} onClick={() => removeProducts(index)}>
                      <img src={icons.commonIconClose} alt={t('common_alt_img', { ns: 'common/base' })} />
                    </IconButton>
                  </Grid2>
                </Grid2>
              </Fragment>
            );
          })}
        </Grid2>
      )}

      {errors?.products?.root?.message && fieldsProducts.length === DEFAULT_NUMBER_ZERO && (
        <Grid2 size={12}>
          <FormHelperText className={cx('errorMessage')}>{errors?.products?.root?.message}</FormHelperText>
        </Grid2>
      )}

      <Grid2 size={12}>
        <Stack
          direction={'row'}
          alignItems={'center'}
          spacing={'4px'}
          className={cx('addFieldGroup')}
          onClick={handleAddItemProducts}
        >
          <img
            src={icons.commonIconAddField}
            alt={t('altImg', { ns: 'common/base' })}
            className={cx('addFieldIcon')}
          />

          <Typography color='primary' className={cx('addField')}>
            {t('common_action_add_field', { ns: 'common/base' })}
          </Typography>
        </Stack>
      </Grid2>
    </Box>
  );
};

export default memo(FieldPOProduct);
